/* Coshi — GSAP motion layer. Injected from snippets/meta-tags.liquid.

   RULES, each learned by breaking it:

   1. Never put two transform tweens on one element. Entrance tweens end with
      clearProps; parallax goes on a CHILD inside overflow:hidden.

   2. One ScrollTrigger per GROUP, not per element.

   3. Motion earns its place. NOTHING mechanical is ever drawn on the deity —
      light responds around him; he is never opened up.

   4. PARKED, NEVER HIDDEN. By request nothing below the hero may be seen in
      place before its slide — not for a frame. So at init (while the loader
      still covers the page) every block still below the fold is PARKED
      beyond its screen edge with a JS-applied transform; the trigger only
      brings it home. The old "text disappears while scrolling" bug came
      from CSS pre-hiding that depended on a callback; this is different in
      every way that mattered:
        - parking is applied by JS, never CSS: no JS, no parking
        - reduced-motion never parks
        - a group whose trigger is already inside the viewport at init is
          left exactly as it is
        - a sweep (400ms for the first 15s, then rAF on every scroll, plus
          load / pageshow / resize / visibilitychange) force-homes any parked
          group whose trigger is on screen, ScrollTrigger or not
        - resize re-measures parking offsets; images inside parked groups
          are switched to eager loading so they arrive already painted
      Worst case is a late slide, never a blank.

   5. Opacity rides from 0.6, never 0.

   THE ENTRANCES ARE EDGE SLIDES, ONE SIDE PER SECTION, slow (2.2s):
   Interactive Series from the left, Try a conversation from the right, the
   app from the left. The ticker (the scrolling strip of pills) and the
   footer stay put, by request. Offsets are measured per element (its own
   edge to the viewport edge, plus a margin), read into numbers before any
   tween — a function-based start value is evaluated more than once by GSAP
   and would see the element already moved. Horizontal overflow is clipped
   on body and the sliding sections so parked blocks never widen the page.

   THE HERO runs its own choreography inside sections/coshi-intro.liquid;
   intro() only keeps the scroll parallax when that markup is present. */
(function () {
  'use strict';

  var HTML = document.documentElement;
  var DIM = 0.6;
  var PAD = 32;
  var SLOW = 2.2;
  var G = null;          /* gsap, once ready */
  var pending = [];      /* parked groups waiting for their trigger */

  function onReady(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else { fn(); }
  }

  function whenGsap(cb) {
    var tries = 0;
    (function poll() {
      if (window.gsap && window.ScrollTrigger) { cb(); return; }
      if (++tries > 120) { HTML.classList.remove('cs-gsap'); return; }
      setTimeout(poll, 50);
    })();
  }

  function fracOf(start) {
    var m = /top\s+(\d+)%/.exec(start || '');
    return m ? parseInt(m[1], 10) / 100 : 0.92;
  }
  function pastStart(el, start) {
    if (!el) return true;
    return el.getBoundingClientRect().top < window.innerHeight * fracOf(start);
  }
  function firedDeep(el, start) {
    if (!el) return true;
    var line = window.innerHeight * fracOf(start);
    return el.getBoundingClientRect().top < line - window.innerHeight * 0.2;
  }

  /* how far an element must move to be fully off the given screen edge */
  function offX(el, side) {
    var r = el.getBoundingClientRect();
    return side === 'right' ? (window.innerWidth - r.left + PAD) : -(r.right + PAD);
  }

  /* ---- parking ---- */
  function park(group) {
    var els = group.els;
    G.set(els, { x: 0 });                          /* measure the natural spot */
    group.offs = els.map(function (el) { return offX(el, group.side); });
    els.forEach(function (el, i) {
      G.set(el, { x: group.offs[i], opacity: DIM, transition: 'none' });
      Array.prototype.forEach.call(el.querySelectorAll('img[loading="lazy"]'), function (img) { img.loading = 'eager'; });
    });
  }
  function unpark(group) {
    var i = pending.indexOf(group);
    if (i > -1) pending.splice(i, 1);
  }
  /* bring a parked group home: slide if it is arriving, snap if it is
     already deep in view (a refresh, a jump, a restored scroll position) */
  function home(group, snap) {
    if (group.done) return;
    group.done = true;
    unpark(group);
    var els = group.els;
    if (snap) { G.set(els, { clearProps: 'all' }); return; }
    els.forEach(function (el, i) {
      var vars = { x: 0, opacity: 1, duration: group.duration || SLOW, ease: 'power3.out', clearProps: 'all' };
      if (group.tl) { group.tl.to(el, vars, i * (group.stagger || 0)); }
      else { vars.delay = i * (group.stagger || 0); G.to(el, vars); }
    });
    if (group.after) group.after();
  }
  function sweep() {
    for (var i = pending.length - 1; i >= 0; i--) {
      var g = pending[i], r = g.trigEl.getBoundingClientRect();
      if (r.top < window.innerHeight && r.bottom > 0) home(g, firedDeep(g.trigEl, g.start));
    }
  }
  function repark() {
    pending.forEach(function (g) { if (!g.done) park(g); });
    sweep();
  }
  function nets() {
    var ticks = 0;
    var iv = setInterval(function () { sweep(); if (++ticks > 36 || !pending.length) clearInterval(iv); }, 400);
    var raf = false;
    window.addEventListener('scroll', function () {
      if (raf || !pending.length) return;
      raf = true;
      requestAnimationFrame(function () { raf = false; sweep(); });
    }, { passive: true });
    var rt = null;
    window.addEventListener('resize', function () { clearTimeout(rt); rt = setTimeout(repark, 150); });
    window.addEventListener('load', sweep);
    window.addEventListener('pageshow', sweep);
    document.addEventListener('visibilitychange', sweep);
  }

  /* an edge slide for a group of elements: parks them now, homes them on the
     trigger. o.edge = 'left' | 'right'. */
  function slide(targets, opts) {
    var els = targets && targets.length !== undefined ? Array.prototype.slice.call(targets) : [targets];
    els = els.filter(Boolean);
    if (!els.length) return null;
    var o = opts || {};
    var trigEl = o.trigger || els[0];
    var start = o.start || 'top 90%';
    if (pastStart(trigEl, start)) return null;   /* already in view: leave it */
    var group = { els: els, side: o.edge || 'left', trigEl: trigEl, start: start,
      duration: o.duration, stagger: o.stagger, tl: o.tl, after: o.after, done: false };
    park(group);
    pending.push(group);
    window.ScrollTrigger.create({
      trigger: trigEl, start: start, once: true,
      onEnter: function () { home(group, firedDeep(trigEl, start)); }
    });
    return group;
  }

  /* a plain rise for things that must not be parked (product page) */
  function reveal(gsap, targets, opts) {
    var els = targets && targets.length !== undefined ? Array.prototype.slice.call(targets) : [targets];
    els = els.filter(Boolean);
    if (!els.length) return;
    var o = opts || {};
    var trigEl = o.trigger || els[0];
    var start = o.start || 'top 88%';
    if (pastStart(trigEl, start)) return;
    window.ScrollTrigger.create({
      trigger: trigEl, start: start, once: true,
      onEnter: function () {
        if (firedDeep(trigEl, start)) return;
        gsap.set(els, { transition: 'none' });
        gsap.fromTo(els,
          { y: o.y != null ? o.y : 56, x: o.x || 0, opacity: DIM, scale: o.scale != null ? o.scale : 1 },
          { y: 0, x: 0, opacity: 1, scale: 1, duration: o.duration || 1,
            stagger: o.stagger || 0, ease: o.ease || 'power3.out', clearProps: 'all' });
      }
    });
  }

  function splitWords(root) {
    if (!root || root.getAttribute('data-cs-split')) return [];
    var out = [], nodes = [], n;
    var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
    while ((n = walker.nextNode())) nodes.push(n);
    nodes.forEach(function (node) {
      if (!node.nodeValue || !node.nodeValue.trim()) return;
      var frag = document.createDocumentFragment();
      node.nodeValue.split(/(\s+)/).forEach(function (part) {
        if (!part) return;
        if (/^\s+$/.test(part)) { frag.appendChild(document.createTextNode(part)); return; }
        var s = document.createElement('span');
        s.className = 'cs-w';
        s.textContent = part;
        frag.appendChild(s);
        out.push(s);
      });
      node.parentNode.replaceChild(frag, node);
    });
    root.setAttribute('data-cs-split', '1');
    return out;
  }

  function utter() {
    try { document.dispatchEvent(new CustomEvent('coshi:utterance')); } catch (e) {}
  }

  function flutterBars(gsap, bars) {
    var rest = [0.28, 0.55, 0.4, 0.6, 0.3];
    bars.forEach(function (b, i) {
      gsap.to(b, {
        scaleY: function () { return gsap.utils.random(0.25, 1); },
        duration: 0.13, repeat: 8, repeatRefresh: true, ease: 'sine.inOut',
        delay: i * 0.028, overwrite: true,
        onComplete: function () {
          gsap.to(b, { scaleY: rest[i] || 0.4, duration: 0.35, ease: 'power2.out' });
        }
      });
    });
  }

  /* ---------- hero ---------- */
  function intro(gsap) {
    var sec = document.querySelector('.ci');
    if (!sec) return;

    var wrap = sec.querySelector('.ci-wrap');
    if (wrap) {
      gsap.to(wrap, {
        yPercent: -9, ease: 'none',
        scrollTrigger: { trigger: sec, start: 'top top', end: 'bottom top', scrub: 0.6 }
      });
    }

    /* the section choreographs its own entrance now */
    if (sec.querySelector('[data-ci-letters], [data-ci-trio]')) return;

    var eye = sec.querySelector('.ci-eye');
    var h1 = sec.querySelector('.ci-h1');
    var band = sec.querySelector('.ci-h1 em');
    var sub = sec.querySelector('.ci-sub');
    var btns = sec.querySelectorAll('.ci-btn');
    if (window.scrollY > window.innerHeight * 0.6) return;

    var tl = gsap.timeline({ delay: 0.1 });
    if (eye) tl.from(eye, { y: -14, opacity: 0, duration: 0.7, clearProps: 'all' }, 0);
    if (h1) {
      var words = splitWords(h1);
      if (words.length) {
        tl.from(words, {
          rotationX: -86, transformPerspective: 900, transformOrigin: '50% 100% -30px',
          yPercent: 42, opacity: 0, duration: 0.95, stagger: 0.045, ease: 'power3.out'
        }, 0.08);
      } else {
        tl.from(h1, { y: 36, opacity: 0, duration: 0.95, clearProps: 'all' }, 0.08);
      }
    }
    if (band) tl.fromTo(band, { '--band': 0 }, { '--band': 1, duration: 0.7, ease: 'power2.inOut' }, 0.5);
    if (sub) tl.from(sub, { y: 16, opacity: 0, duration: 0.7, clearProps: 'all' }, 0.62);
    if (btns.length) {
      tl.from(btns, { scale: 0.88, opacity: 0, duration: 0.6, stagger: 0.09,
        ease: 'back.out(2.2)', clearProps: 'all' }, 1.2);
    }
  }

  /* ---------- section 2, the three products: everything from the LEFT ---------- */
  function launch(gsap) {
    var sec = document.querySelector('.cl');
    if (!sec) return;
    var head = sec.querySelector('.cl-head');
    var grid = sec.querySelector('.cl-grid');
    var cards = gsap.utils.toArray('.cl-card');
    var fine = sec.querySelector('.cl-fine');

    if (head) slide(head, { edge: 'left' });
    if (cards.length && grid) slide(cards, { trigger: grid, start: 'top 88%', edge: 'left', stagger: 0.1 });
    gsap.utils.toArray('.cl-card__media img').forEach(function (img) {
      gsap.fromTo(img, { yPercent: -3 }, {
        yPercent: 3, ease: 'none',
        scrollTrigger: { trigger: img.closest('.cl-card') || img, start: 'top bottom', end: 'bottom top', scrub: 1 }
      });
    });
    if (fine) slide(fine, { start: 'top 96%', edge: 'left' });
  }

  /* ---------- section 3, "Go on — ask him": everything from the RIGHT ----------
     Each exchange carries a language tag; it is swapped beside his name so the
     "he answers in the language you used" claim is shown, not just stated. */
  function ask(gsap, motion) {
    var sec = document.querySelector('.cq');
    if (!sec) return;

    var reduce = false;
    try { reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) {}

    if (motion && !reduce) {
      slide(sec.querySelector('.cq-head'), { edge: 'right' });
      slide(sec.querySelector('.cq-figwrap'), { edge: 'right' });
      slide(sec.querySelector('.cq-talk'), { edge: 'right' });
      slide(sec.querySelectorAll('.cq-gift'), { trigger: sec.querySelector('.cq-gifts'), edge: 'right', stagger: 0.1 });
      slide(sec.querySelector('.cq-fine'), { start: 'top 96%', edge: 'right' });
    }
    if (!motion) return;   /* the wiring below runs once, from the first call */

    var data = null;
    try { data = JSON.parse(sec.querySelector('[data-cq-data]').textContent); } catch (e) {}
    if (!data || !data.exchanges || !data.exchanges.length) return;

    var qWho = sec.querySelector('[data-cq-qwho]');
    var qEl = sec.querySelector('[data-cq-q]');
    var aEl = sec.querySelector('[data-cq-a]');
    var aLang = sec.querySelector('[data-cq-alang]');
    var cite = sec.querySelector('[data-cq-cite]');
    var citeB = sec.querySelector('[data-cq-citeb]');
    var citeE = sec.querySelector('[data-cq-citee]');
    var halo = sec.querySelector('[data-cq-halo]');
    var bars = sec.querySelectorAll('.cq-wave i');
    var fig = sec.querySelector('[data-cq-fig]');
    var hint = sec.querySelector('[data-cq-hint]');
    var chips = Array.prototype.slice.call(sec.querySelectorAll('.cq-chip'));
    var spkT = null;

    function speak(who, q, a, c, roman, lang) {
      if (qWho) qWho.textContent = who;
      if (qEl) qEl.textContent = q;
      if (aEl) aEl.textContent = a;
      if (aLang) {
        aLang.textContent = lang || '';
        aLang.style.display = lang ? '' : 'none';
      }
      if (cite) {
        if (c) {
          cite.style.display = '';
          if (citeB) citeB.textContent = c;
          if (citeE) citeE.textContent = roman || '';
        } else { cite.style.display = 'none'; }
      }

      sec.classList.add('is-speaking');
      clearTimeout(spkT);
      spkT = setTimeout(function () { sec.classList.remove('is-speaking'); }, 2600);
      utter();

      if (reduce) return;
      if (qEl) gsap.fromTo(qEl, { opacity: 0.3 }, { opacity: 1, duration: 0.4, overwrite: 'auto' });
      if (aEl) gsap.fromTo(aEl, { clipPath: 'inset(0 100% 0 0)' },
        { clipPath: 'inset(0 0% 0 0)', duration: 0.9, ease: 'power2.out', clearProps: 'clipPath', overwrite: 'auto' });
      if (halo) gsap.fromTo(halo, { scale: 1, opacity: 0.75 },
        { scale: 2.9, opacity: 0, duration: 0.9, ease: 'power2.out', overwrite: 'auto' });
      if (c && cite) gsap.fromTo(cite, { opacity: 0 }, { opacity: 1, delay: 0.5, duration: 0.5, overwrite: 'auto' });
      if (bars.length) flutterBars(gsap, bars);
    }

    chips.forEach(function (chip) {
      chip.addEventListener('click', function () {
        var i = parseInt(chip.getAttribute('data-i'), 10) || 0;
        var x = data.exchanges[i];
        if (!x) return;
        chips.forEach(function (c) { c.setAttribute('aria-pressed', c === chip ? 'true' : 'false'); });
        speak('A child', x.q, x.a, x.cite, x.roman, x.lang);
      });
    });

    function hello() {
      chips.forEach(function (c) { c.setAttribute('aria-pressed', 'false'); });
      speak('You', '(a hand rests on his palm)', data.greet || 'Radhe Radhe!', '', '', data.greetLang);
      if (hint) {
        if (reduce) { hint.style.display = 'none'; }
        else {
          gsap.to(hint, { opacity: 0, y: 6, duration: 0.4, overwrite: 'auto',
            onComplete: function () { hint.style.display = 'none'; } });
        }
      }
    }
    if (fig) {
      fig.addEventListener('click', hello);
      fig.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); hello(); }
      });
    }
  }

  /* ---------- section 4, the parent app: everything from the LEFT ---------- */
  function appSection(gsap) {
    var sec = document.querySelector('.ca');
    if (!sec) return;

    var band = sec.querySelector('.ca-h2 em');
    var head = sec.querySelector('.ca-head') || sec.querySelector('.ca-h2');
    if (head) {
      slide(head, { start: 'top 88%', edge: 'left', after: function () {
        if (band) gsap.fromTo(band, { '--band': 0 }, { '--band': 1, duration: 0.7, delay: 1.5, ease: 'power2.inOut' });
      } });
    }

    var cols = gsap.utils.toArray('.ca-col');
    var row = sec.querySelector('.ca-row');
    if (cols.length && row) {
      var tl = gsap.timeline();
      slide(cols, { trigger: row, start: 'top 88%', edge: 'left', stagger: 0.1, tl: tl, after: function () {
        gsap.utils.toArray('.ap-track i').forEach(function (fill, i) {
          if ((fill.style.width || '0%') === '0%') return;
          tl.fromTo(fill, { scaleX: 0, transformOrigin: 'left center' },
            { scaleX: 1, duration: 1.05, ease: 'power2.out' }, 1.4 + i * 0.07);
        });
        gsap.utils.toArray('.ap-spark i').forEach(function (bar, i) {
          tl.fromTo(bar, { scaleY: 0, transformOrigin: 'bottom center' },
            { scaleY: 1, duration: 0.7, ease: 'back.out(1.6)' }, 1.45 + i * 0.06);
        });
        gsap.utils.toArray('.ap-ringbar').forEach(function (ring) {
          var to = parseFloat(ring.getAttribute('data-dash') || '0');
          tl.fromTo(ring, { strokeDashoffset: 163.4 },
            { strokeDashoffset: to, duration: 1.2, ease: 'power2.out' }, 1.5);
        });
        gsap.utils.toArray('.ca .ap-msgs > *').forEach(function (m, i) {
          tl.fromTo(m, { y: 14, opacity: DIM },
            { y: 0, opacity: 1, duration: 0.5, ease: 'power3.out', clearProps: 'all' }, 1.55 + i * 0.12);
        });
      } });
    }

    var foot = sec.querySelector('.ca-foot');
    if (foot) slide(foot, { start: 'top 96%', edge: 'left' });
  }

  function cartNote(gsap) {
    var sec = document.querySelector('.ccn');
    if (!sec) return;
    slide(sec.querySelectorAll('.ccn-step'), { trigger: sec, edge: 'left', stagger: 0.1 });
  }

  function prebook(gsap) {
    var btns = document.querySelectorAll('.cs-prebook__btn');
    Array.prototype.forEach.call(btns, function (btn, i) {
      window.ScrollTrigger.create({
        trigger: btn, start: 'top 94%', once: true,
        onEnter: function () {
          gsap.timeline({ delay: 0.6 + i * 0.12 })
            .to(btn, { scale: 1.03, duration: 0.42, ease: 'power2.out' })
            .to(btn, { scale: 1, duration: 0.6, ease: 'elastic.out(1, 0.5)' });
        }
      });
      btn.addEventListener('pointerdown', function () { gsap.to(btn, { scale: 0.97, duration: 0.12 }); });
      ['pointerup', 'pointerleave'].forEach(function (ev) {
        btn.addEventListener(ev, function () { gsap.to(btn, { scale: 1, duration: 0.28, ease: 'back.out(2)' }); });
      });
    });
  }

  function productPage(gsap) {
    var card = document.querySelector('.cs-prebook');
    if (card) reveal(gsap, card, { start: 'top 94%', y: 24, duration: 0.85 });
  }

  function hover(gsap) {
    gsap.utils.toArray('.cl-card').forEach(function (card) {
      card.addEventListener('mouseenter', function () {
        gsap.to(card, { y: -9, duration: 0.4, ease: 'power2.out', overwrite: 'auto' });
      });
      card.addEventListener('mouseleave', function () {
        gsap.to(card, { y: 0, duration: 0.5, ease: 'power2.out', overwrite: 'auto' });
      });
    });
    gsap.utils.toArray('.ph').forEach(function (ph) {
      ph.addEventListener('mouseenter', function () {
        gsap.to(ph, { y: -12, scale: 1.02, duration: 0.45, ease: 'power2.out', overwrite: 'auto' });
      });
      ph.addEventListener('mouseleave', function () {
        gsap.to(ph, { y: 0, scale: 1, duration: 0.55, ease: 'power2.out', overwrite: 'auto' });
      });
    });
  }

  function anchors() {
    var reduce = false;
    try { reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) {}
    document.addEventListener('click', function (e) {
      var a = e.target.closest ? e.target.closest('a[href^="#"]') : null;
      if (!a) return;
      var id = a.getAttribute('href');
      if (!id || id.length < 2) return;
      var target = document.querySelector(id);
      if (!target) return;
      e.preventDefault();
      target.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
    });
  }

  function init() {
    var gsap = window.gsap, ST = window.ScrollTrigger;
    G = gsap;
    gsap.registerPlugin(ST);
    gsap.defaults({ ease: 'power3.out', duration: 0.9 });

    anchors();

    var mm = gsap.matchMedia();

    mm.add('(prefers-reduced-motion: reduce)', function () {
      gsap.set('.ci-h1 em, .ca-h2 em', { '--band': 1 });
      ask(gsap, true);   /* interactive wiring, no slides */
    });

    mm.add('(prefers-reduced-motion: no-preference)', function () {
      /* parked blocks must never widen the page */
      gsap.set([document.body, '.cl', '.cq', '.ca', '.ccn'], { overflowX: 'clip' });
      intro(gsap);
      launch(gsap);
      ask(gsap, true);
      appSection(gsap);
      cartNote(gsap);
      prebook(gsap);
      productPage(gsap);
      nets();
    });

    mm.add('(hover: hover) and (pointer: fine)', function () { hover(gsap); });

    window.addEventListener('load', function () { ST.refresh(); });
    document.addEventListener('shopify:section:load', function () { ST.refresh(); });
  }

  onReady(function () { whenGsap(init); });
})();
